import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getCoachPlanningMonitoring, getCoachPlanningMonitoringList, updateCoachPlanningMonitorLap, updateCoachPlanningMonitorReset, updateCoachPlanningMonitorShovelfuls, updateCoachPlanningMonitorStart, updateCoachPlanningMonitorFinish, updateCoachPlanningMonitorComments, updateCoachPlanningMonitorManual, deleteCoachPlanningMonitoring, deleteCoachPlanningMonitorEvent, updateCoachPlanningMonitorEvent, exportMonitoringPDF, MonitoringSeriesImages } from "../../../../services/planning";
import { buildSeriesBlocks, formatDuration as formatSeriesDuration, SeriesBlock } from "../../../../services/monitoring-series";
import { Button, Form, Modal, Row, Col } from "react-bootstrap";
import { t } from "i18next";
import { useStopwatch } from 'react-timer-hook';
import moment from 'moment';
import * as htmlToImage from 'html-to-image';
import type ReactECharts from 'echarts-for-react';
import { LoadingSpinner } from "../../../general/loading-spinner";
import { MonitoringShovelfulsChart } from "../../../charts/monitoring-shovelfuls-chart";
import { MonitoringLaptimesChart } from "../../../charts/monitoring-laptimes-chart";
import { getTeamSeasonAthletes } from "../../../../services/athlete";
import { getSeasonCurrent } from "../../../../services/season";

export function CoachTrainingMonitoring() {
    const params: any = useParams();

    const [monitoringId, setMonitoringId] = useState<number | null>(null);
    const [monitoringList, setMonitoringList] = useState<any[]>([]);
    const [showMonitoringSelector, setShowMonitoringSelector] = useState(false);
    const [monitoringMeta, setMonitoringMeta] = useState<any>(null);
    const [monitoringEvents, setMonitoringEvents] = useState<any[]>([]);
    const [monitoringAthletes, setMonitoringAthletes] = useState<number[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [startDateTime, setStartDateTime] = useState(new Date());
    const [monitoringState, setMonitoringState] = useState<'no_monitoring' | 'monitoring' | 'pause' | 'finished'>('no_monitoring');
    const [isRecordingShovelfuls, setIsRecordingShovelfuls] = useState(false);
    const [shovelfulsStartTime, setShovelfulsStartTime] = useState<Date | null>(null);
    const [shovelfulsCount, setShovelfulsCount] = useState(4);
    const [shovelfulsRecords, setShovelfulsRecords] = useState<string[]>([]);
    const [lastLapTime, setLastLapTime] = useState<string | null>(null);
    const [previousLapTime, setPreviousLapTime] = useState<string | null>(null);
    const [lastLapDiff, setLastLapDiff] = useState<string | null>(null);
    const [previousLapDiff, setPreviousLapDiff] = useState<string | null>(null);
    const [lastLapTotalSeconds, setLastLapTotalSeconds] = useState<number>(0);
    const [previousLapTotalSeconds, setPreviousLapTotalSeconds] = useState<number>(0);
    const [lastLapEventId, setLastLapEventId] = useState<number | null>(null);
    const [previousLapEventId, setPreviousLapEventId] = useState<number | null>(null);
    const [lastLapKind, setLastLapKind] = useState<'work' | 'rest' | null>(null);
    const [previousLapKind, setPreviousLapKind] = useState<'work' | 'rest' | null>(null);
    const [confirmDeleteLastLap, setConfirmDeleteLastLap] = useState(false);

    // Estados para selección de atletas
    const [showAthleteSelector, setShowAthleteSelector] = useState(false);
    const [availableAthletes, setAvailableAthletes] = useState<any[]>([]);
    const [selectedAthleteIds, setSelectedAthleteIds] = useState<number[]>([]);
    const [currentSeasonId, setCurrentSeasonId] = useState<number>(0);
    const [athleteSearchTerm, setAthleteSearchTerm] = useState('');
    const [showAthleteNames, setShowAthleteNames] = useState(false);
    const [postSessionComments, setPostSessionComments] = useState<string>('');
    const [isEditingComments, setIsEditingComments] = useState(false);

    // Monitoring list athlete visibility
    const [expandedMonitoringAthletes, setExpandedMonitoringAthletes] = useState<number[]>([]);

    // Edit lap modal
    const [showEditLapModal, setShowEditLapModal] = useState(false);
    const [editingLap, setEditingLap] = useState<any>(null);
    const [editLapMinutes, setEditLapMinutes] = useState('');
    const [editLapSeconds, setEditLapSeconds] = useState('');
    const [editLapMilliseconds, setEditLapMilliseconds] = useState('');
    const [editLapKind, setEditLapKind] = useState<'work' | 'rest'>('work');

    // Manual entry mode
    const [entryMode, setEntryMode] = useState<'realtime' | 'manual'>('realtime');
    const [manualStartTime, setManualStartTime] = useState('');
    const [manualDuration, setManualDuration] = useState('01:00:00.000');
    const [manualLapEntries, setManualLapEntries] = useState<{time: string, kind: 'work' | 'rest'}[]>([]);
    const [manualShovefulEntries, setManualShovefulEntries] = useState<{time: string, strokesPerMinute: number}[]>([]);
    const [manualComments, setManualComments] = useState<string>('');
    const [manualSelectedAthletes, setManualSelectedAthletes] = useState<number[]>([]);

    // PDF export
    const [exportingPDF, setExportingPDF] = useState(false);
    const resultsRef = useRef<HTMLDivElement>(null);
    // Refs per series index (1-based)
    const seriesSummaryRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const seriesShovelfulsRefs = useRef<Record<number, ReactECharts | null>>({});
    const seriesLaptimesRefs = useRef<Record<number, ReactECharts | null>>({});

    const {
        totalSeconds,
        milliseconds,
        seconds,
        minutes,
        hours,
        days,
        isRunning,
        start,
        pause,
        reset
    } = useStopwatch({autoStart: false, interval: 20 });
 
    useEffect(() => {
        Promise.all([
            getCoachPlanningMonitoringList(params.team, params.day, params.day_moment, params.day_moment_session),
            getSeasonCurrent()
        ])
            .then(([listResponse, seasonResponse]) => {
                const monitoringSessions = listResponse.data || [];
                const season = seasonResponse.data;

                // Guardar lista de monitorizaciones
                setMonitoringList(monitoringSessions);

                // Guardar season_id para usarlo después
                if (season && season.id) {
                    setCurrentSeasonId(season.id);

                    // Cargar atletas del equipo para esta temporada
                    getTeamSeasonAthletes(params.team, season.id)
                        .then(athletesResponse => {
                            const athletes = athletesResponse.data || [];
                            setAvailableAthletes(athletes);

                            // Preseleccionar todos los atletas por defecto
                            const allAthleteIds = athletes.map((a: any) => a.athlete_id);
                            setSelectedAthleteIds(allAthleteIds);
                        })
                        .catch(error => {
                            console.error('Error loading athletes:', error);
                            setAvailableAthletes([]);
                        });
                }

                // Si hay monitorizaciones existentes, mostrar el selector
                if (monitoringSessions.length > 0) {
                    setShowMonitoringSelector(true);
                } else {
                    // No hay monitorizaciones, estado inicial para crear una nueva
                    setMonitoringMeta(null);
                    setMonitoringEvents([]);
                    setMonitoringAthletes([]);
                }

                setLoaded(true);
            })
            .catch(error => {
                console.error('Error loading monitoring data:', error);
                setLoaded(true);
            });
    }, []);

    // Cargar una monitorización existente
    const loadMonitoringSession = (monitoring_id: number) => {
        getCoachPlanningMonitoring(params.team, params.day, params.day_moment, params.day_moment_session, monitoring_id)
            .then(response => {
                const data = response.data;

                if (data && data.meta) {
                    setMonitoringId(data.meta.id);
                    setMonitoringMeta(data.meta);
                    setMonitoringEvents(data.events || []);
                    setMonitoringAthletes(data.athletes || []);

                    // Usar sample_shovelfuls del meta si existe
                    if (data.meta.sample_shovelfuls) {
                        setShovelfulsCount(data.meta.sample_shovelfuls);
                    }

                    // Cargar comentarios del meta
                    if (data.meta.observations) {
                        setPostSessionComments(data.meta.observations);
                    }

                    // Si hay eventos, verificar si la sesión está finalizada
                    if (data.events && data.events.length > 0) {
                        const hasFinishEvent = data.events.some((event: any) => event.event_type === 'Training finish');
                        if (hasFinishEvent) {
                            setMonitoringState('finished');
                        }
                    }
                }

                setShowMonitoringSelector(false);
            })
            .catch(error => {
                console.error('Error loading monitoring session:', error);
                alert(t("Error loading monitoring session"));
            });
    };

    // Crear una nueva monitorización
    const createNewMonitoring = () => {
        setMonitoringId(null);
        setMonitoringMeta(null);
        setMonitoringEvents([]);
        setMonitoringAthletes([]);
        setMonitoringState('no_monitoring');
        setPostSessionComments('');
        setIsEditingComments(false);
        setShowMonitoringSelector(false);

        // Resetear cronómetro
        setStartDateTime(new Date());
        reset(new Date(), false);

        // Resetear parciales
        setLastLapTime(null);
        setPreviousLapTime(null);
        setLastLapDiff(null);
        setPreviousLapDiff(null);
        setLastLapTotalSeconds(0);
        setPreviousLapTotalSeconds(0);

        // Resetear paladas
        setIsRecordingShovelfuls(false);
        setShovelfulsStartTime(null);
        setShovelfulsCount(4);
        setShovelfulsRecords([]);

        // Resetear modo de entrada
        setEntryMode('realtime');

        // Reseleccionar todos los atletas
        const allAthleteIds = availableAthletes.map((a: any) => a.athlete_id);
        setSelectedAthleteIds(allAthleteIds);
    };

    // Volver a la lista de monitorizaciones
    const backToMonitoringList = () => {
        // Recargar la lista de monitorizaciones
        getCoachPlanningMonitoringList(params.team, params.day, params.day_moment, params.day_moment_session)
            .then(response => {
                const monitoringSessions = response.data || [];
                setMonitoringList(monitoringSessions);
                setShowMonitoringSelector(true);
            })
            .catch(error => {
                console.error('Error loading monitoring list:', error);
            });
    };

    // Eliminar una monitorización
    const handleDeleteMonitoring = (monitoring_id: number) => {
        const confirmDelete = window.confirm(t("Are you sure you want to delete this monitoring session? This action cannot be undone."));

        if (!confirmDelete) {
            return;
        }

        deleteCoachPlanningMonitoring(monitoring_id)
            .then(response => {
                if (response.data.status == 1) {
                    // Recargar la lista después de eliminar
                    return getCoachPlanningMonitoringList(params.team, params.day, params.day_moment, params.day_moment_session);
                } else {
                    alert(response.data.message || t("Error deleting monitoring session"));
                    throw new Error(response.data.message);
                }
            })
            .then(response => {
                const monitoringSessions = response.data || [];
                setMonitoringList(monitoringSessions);

                // Si no quedan monitorizaciones, ocultar el selector
                if (monitoringSessions.length === 0) {
                    setShowMonitoringSelector(false);
                }
            })
            .catch(error => {
                console.error('Error deleting monitoring:', error);
                alert(t("Error deleting monitoring session"));
            });
    };

    // Función auxiliar para construir body con monitoring_id o parámetros legacy
    const buildMonitoringBody = (extraParams: any = {}) => {
        if (monitoringId) {
            return {
                monitoring_id: monitoringId,
                ...extraParams
            };
        } else {
            return {
                team_id: params.team,
                day: params.day,
                day_moment: params.day_moment,
                day_moment_session: params.day_moment_session,
                ...extraParams
            };
        }
    };

    const handleOpenAthleteSelector = () => {
        // Preseleccionar todos los atletas por defecto si no hay ninguno seleccionado
        if (selectedAthleteIds.length === 0) {
            const allAthleteIds = availableAthletes.map((a: any) => a.athlete_id);
            setSelectedAthleteIds(allAthleteIds);
        }
        setShowAthleteSelector(true);
    };

    const handleCloseAthleteSelector = () => {
        setShowAthleteSelector(false);
        setAthleteSearchTerm('');
    };

    const handleToggleAthlete = (athleteId: number) => {
        if (selectedAthleteIds.includes(athleteId)) {
            setSelectedAthleteIds(selectedAthleteIds.filter(id => id !== athleteId));
        } else {
            setSelectedAthleteIds([...selectedAthleteIds, athleteId]);
        }
    };

    const getFilteredAthletes = () => {
        return availableAthletes.filter((athlete: any) =>
            athleteSearchTerm === '' ||
            athlete.athlete_name.toLowerCase().includes(athleteSearchTerm.toLowerCase())
        );
    };

    const handleToggleAllAthletes = () => {
        const filteredAthletes = getFilteredAthletes();
        const filteredIds = filteredAthletes.map((a: any) => a.athlete_id);
        const allFilteredSelected = filteredIds.every(id => selectedAthleteIds.includes(id));

        if (allFilteredSelected) {
            // Deseleccionar solo los filtrados
            setSelectedAthleteIds(selectedAthleteIds.filter(id => !filteredIds.includes(id)));
        } else {
            // Añadir los filtrados a la selección actual (sin duplicados)
            const newSelection = Array.from(new Set([...selectedAthleteIds, ...filteredIds]));
            setSelectedAthleteIds(newSelection);
        }
    };

    const handleConfirmStart = () => {
        if (selectedAthleteIds.length === 0) {
            alert(t("Please select at least one athlete"));
            return;
        }

        const startDate = new Date();
        if (monitoringState === 'no_monitoring') {
            const body_start: any = {
                team_id: params.team,
                day: params.day,
                day_moment: params.day_moment,
                day_moment_session: params.day_moment_session,
                event_time: moment(startDate).format('Y-MM-DD HH:mm:ss.SSS'),
                athlete_ids: selectedAthleteIds,
                sample_shovelfuls: shovelfulsCount
            };

            // Si ya hay un monitoring_id, incluirlo para reemplazar esa sesión
            if (monitoringId) {
                body_start.monitoring_id = monitoringId;
            }

            updateCoachPlanningMonitorStart(body_start)
                .then(response => {
                    if (response.data.status != 1) {
                        alert(response.data.message);
                    } else {
                        // Guardar el monitoring_id de la respuesta
                        if (response.data.meta_id) {
                            setMonitoringId(response.data.meta_id);
                        }
                        // Actualizar monitoringAthletes con los seleccionados
                        setMonitoringAthletes(selectedAthleteIds);
                        // Resetear comentarios post-sesión para la nueva sesión
                        setPostSessionComments('');
                        setIsEditingComments(false);
                    }
                })
                .catch(error => {
                    console.log(error);
                    alert(t("Error starting monitoring session"));
                });
        }
        setStartDateTime(startDate);
        setMonitoringState('monitoring');
        setShowAthleteSelector(false);
        start();
    };

    const handlePause = () => {
        pause();
        setMonitoringState('pause');
    }

    const handleResume = () => {
        start();
        setMonitoringState('monitoring');
    }

    const handleFinish = () => {
        pause();

        // Si está registrando paladas, finalizarlas primero
        if (isRecordingShovelfuls) {
            handleFinishShovelfulsRecording();
        }

        // Registrar el último parcial antes de finalizar
        const currentTotalSeconds = totalSeconds + (milliseconds / 1000);
        const mins = Math.floor(currentTotalSeconds / 60);
        const secs = Math.floor(currentTotalSeconds % 60);
        const millis = milliseconds;
        const lapTimeFormatted = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${millis.toString().padStart(3, '0')}`;

        const lapDiffSeconds = lastLapTotalSeconds > 0
            ? currentTotalSeconds - lastLapTotalSeconds
            : currentTotalSeconds;

        const diffMins = Math.floor(lapDiffSeconds / 60);
        const diffSecs = Math.floor(lapDiffSeconds % 60);
        const diffMillis = Math.floor((lapDiffSeconds % 1) * 1000);
        const lapDiffFormatted = `${diffMins.toString().padStart(2, '0')}:${diffSecs.toString().padStart(2, '0')}:${diffMillis.toString().padStart(3, '0')}`;

        setPreviousLapTime(lastLapTime);
        setLastLapTime(lapTimeFormatted);
        setPreviousLapDiff(lastLapDiff);
        setLastLapDiff(lapDiffFormatted);
        setPreviousLapTotalSeconds(lastLapTotalSeconds);
        setLastLapTotalSeconds(currentTotalSeconds);

        const eventTime = moment().format('Y-MM-DD HH:mm:ss.SSS');

        const body_lap = buildMonitoringBody({
            event_time: eventTime,
            event_type: 'lap'
        });

        // Registrar parcial y luego finalizar
        updateCoachPlanningMonitorLap(body_lap)
            .then(() => {
                const body_finish = buildMonitoringBody({
                    event_time: eventTime
                });
                return updateCoachPlanningMonitorFinish(body_finish);
            })
            .then(() => {
                // Recargar los datos de monitorización para mostrar el resumen
                return getCoachPlanningMonitoring(params.team, params.day, params.day_moment, params.day_moment_session, monitoringId || undefined);
            })
            .then(response => {
                const data = response.data;
                if (data && data.meta) {
                    setMonitoringMeta(data.meta);
                    setMonitoringEvents(data.events || []);
                    setMonitoringAthletes(data.athletes || []);
                }
                setMonitoringState('finished');
            })
            .catch(error => {
                console.log(error);
                setMonitoringState('finished');
            });
    }

    const handleReset = () => {
        // Confirmar antes de eliminar todos los datos
        const confirmReset = window.confirm(t("Are you sure you want to reset? All monitoring data will be deleted and this action cannot be undone."));

        if (!confirmReset) {
            return;
        }

        setStartDateTime(new Date());
        setMonitoringState('no_monitoring');
        setIsRecordingShovelfuls(false);
        setShovelfulsStartTime(null);
        setShovelfulsCount(4);
        setShovelfulsRecords([]);
        setLastLapTime(null);
        setPreviousLapTime(null);
        setLastLapDiff(null);
        setPreviousLapDiff(null);
        setLastLapTotalSeconds(0);
        setPreviousLapTotalSeconds(0);
        setPostSessionComments('');
        setIsEditingComments(false);
        reset(new Date(), false);

        const body = buildMonitoringBody();
        updateCoachPlanningMonitorReset(body)
            .then(response => {
                if (response.data.status != 1) {
                    alert(response.data.message);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleStartShovelfulsRecording = () => {
        setShovelfulsStartTime(new Date());
        setIsRecordingShovelfuls(true);
    }

    const handleFinishShovelfulsRecording = () => {
        if (!shovelfulsStartTime) return;

        const endTime = new Date();
        const durationMs = endTime.getTime() - shovelfulsStartTime.getTime();

        // Calcular paladas por minuto usando el intervalo y las paladas contadas
        // Si contamos 4 entradas (1, 2, 3, 4) en el agua, son 3 ciclos completos de palada
        const actualStrokes = shovelfulsCount - 1;
        const durationMinutes = durationMs / 60000;
        const strokesPerMin = actualStrokes / durationMinutes;
        const strokesPerMinRounded = parseFloat(strokesPerMin.toFixed(1));

        // Guardar las paladas por minuto con un decimal
        const recordValue = strokesPerMinRounded.toFixed(1);

        // Añadir al array de registros local para mostrar en la UI
        const newRecords = [...shovelfulsRecords, recordValue];
        setShovelfulsRecords(newRecords);

        // Guardar en la base de datos - solo este registro individual
        const body = buildMonitoringBody({
            event_time: moment().format('Y-MM-DD HH:mm:ss.SSS'),
            event_type: 'Shovelful',
            event_value: recordValue
        });

        updateCoachPlanningMonitorShovelfuls(body)
            .then(response => {
                if (response.data.status != 1) {
                    alert(response.data.message);
                }
            })
            .catch(error => {
                console.log(error);
            });

        // Reset estado
        setIsRecordingShovelfuls(false);
        setShovelfulsStartTime(null);
    }

    const handleChangeShovelfulsCount = (delta: number) => {
        const newCount = Math.max(1, shovelfulsCount + delta);
        setShovelfulsCount(newCount);
    }

    const handleLapClick = (kind: 'work' | 'rest' = 'work') => {
        // Calcular el tiempo acumulado del cronómetro (incluyendo milisegundos)
        const currentTotalSeconds = totalSeconds + (milliseconds / 1000);
        const mins = Math.floor(currentTotalSeconds / 60);
        const secs = Math.floor(currentTotalSeconds % 60);
        const millis = milliseconds;
        const lapTimeFormatted = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${millis.toString().padStart(3, '0')}`;

        // Calcular la diferencia con el parcial anterior
        const lapDiffSeconds = lastLapTotalSeconds > 0
            ? currentTotalSeconds - lastLapTotalSeconds
            : currentTotalSeconds;

        const diffMins = Math.floor(lapDiffSeconds / 60);
        const diffSecs = Math.floor(lapDiffSeconds % 60);
        const diffMillis = Math.floor((lapDiffSeconds % 1) * 1000);
        const lapDiffFormatted = `${diffMins.toString().padStart(2, '0')}:${diffSecs.toString().padStart(2, '0')}:${diffMillis.toString().padStart(3, '0')}`;

        // Actualizar los tiempos de parciales
        setPreviousLapTime(lastLapTime);
        setLastLapTime(lapTimeFormatted);
        setPreviousLapDiff(lastLapDiff);
        setLastLapDiff(lapDiffFormatted);
        setPreviousLapTotalSeconds(lastLapTotalSeconds);
        setLastLapTotalSeconds(currentTotalSeconds);
        setPreviousLapEventId(lastLapEventId);
        setPreviousLapKind(lastLapKind);
        setLastLapKind(kind);
        setConfirmDeleteLastLap(false);

        const body = buildMonitoringBody({
            event_time: moment().format('Y-MM-DD HH:mm:ss.SSS'),
            event_type: 'lap',
            event_value: kind === 'rest' ? 'rest' : null
        });
        updateCoachPlanningMonitorLap(body)
            .then(response => {
                if (response.data.status != 1) {
                    alert(response.data.message);
                } else if (response.data.event_id) {
                    setLastLapEventId(response.data.event_id);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleRestClick = () => handleLapClick('rest');

    const handleDeleteLastLap = () => {
        if (!lastLapEventId) return;

        deleteCoachPlanningMonitorEvent(lastLapEventId)
            .then(response => {
                if (response.data.status == 1) {
                    // Restaurar el estado al parcial anterior
                    setLastLapTime(previousLapTime);
                    setLastLapDiff(previousLapDiff);
                    setLastLapTotalSeconds(previousLapTotalSeconds);
                    setLastLapEventId(previousLapEventId);
                    setLastLapKind(previousLapKind);
                    setPreviousLapTime(null);
                    setPreviousLapDiff(null);
                    setPreviousLapTotalSeconds(0);
                    setPreviousLapEventId(null);
                    setPreviousLapKind(null);
                    setConfirmDeleteLastLap(false);
                } else {
                    alert(response.data.message);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleSaveComments = () => {
        const body = buildMonitoringBody({
            coach_comments: postSessionComments
        });

        updateCoachPlanningMonitorComments(body)
            .then(response => {
                if (response.data.status == 1) {
                    // Actualizar el meta local
                    setMonitoringMeta({
                        ...monitoringMeta,
                        observations: postSessionComments
                    });
                    setIsEditingComments(false);
                } else {
                    alert(response.data.message || t("Error saving comments"));
                }
            })
            .catch(error => {
                console.log(error);
                alert(t("Error saving comments"));
            });
    }

    const handleDeleteLapEvent = (event_id: number) => {
        const confirmDelete = window.confirm(t("Are you sure you want to delete this lap? The remaining laps will be recalculated."));
        if (!confirmDelete) {
            return;
        }

        deleteCoachPlanningMonitorEvent(event_id)
            .then(response => {
                if (response.data.status == 1) {
                    // Recargar los datos de monitorización para recalcular el resumen
                    return getCoachPlanningMonitoring(params.team, params.day, params.day_moment, params.day_moment_session, monitoringId || undefined);
                } else {
                    alert(response.data.message || t("Error deleting lap"));
                    throw new Error(response.data.message);
                }
            })
            .then(response => {
                const data = response.data;
                if (data && data.meta) {
                    setMonitoringMeta(data.meta);
                    setMonitoringEvents(data.events || []);
                    setMonitoringAthletes(data.athletes || []);
                }
            })
            .catch(error => {
                console.error('Error deleting lap event:', error);
            });
    }

    const handleEditLapClick = (lap: any) => {
        setEditingLap(lap);
        // Descomponer elapsedSeconds en minutos, segundos y milisegundos
        const totalSecs = lap.elapsedSeconds;
        const mins = Math.floor(totalSecs / 60);
        const secs = Math.floor(totalSecs % 60);
        const millis = Math.round((totalSecs % 1) * 1000);
        setEditLapMinutes(mins.toString().padStart(2, '0'));
        setEditLapSeconds(secs.toString().padStart(2, '0'));
        setEditLapMilliseconds(millis.toString().padStart(3, '0'));
        setEditLapKind(lap.kind === 'rest' ? 'rest' : 'work');
        setShowEditLapModal(true);
    }

    const handleSaveEditLap = () => {
        if (!editingLap) return;

        const summary = getSessionSummary();
        if (!summary || !summary.startTime) return;

        // Calcular el nuevo event_time sumando el tiempo editado al start event
        const startEvent = monitoringEvents.find((event: any) => event.event_type === 'Training start');
        if (!startEvent) return;

        const mins = parseInt(editLapMinutes) || 0;
        const secs = parseInt(editLapSeconds) || 0;
        const millis = parseInt(editLapMilliseconds) || 0;

        const newEventTime = moment(startEvent.event_time)
            .add(mins, 'minutes')
            .add(secs, 'seconds')
            .add(millis, 'milliseconds')
            .format('Y-MM-DD HH:mm:ss.SSS');

        updateCoachPlanningMonitorEvent(editingLap.id, newEventTime, editLapKind === 'rest' ? 'rest' : null)
            .then(response => {
                if (response.data.status == 1) {
                    setShowEditLapModal(false);
                    setEditingLap(null);
                    // Recargar los datos para recalcular
                    return getCoachPlanningMonitoring(params.team, params.day, params.day_moment, params.day_moment_session, monitoringId || undefined);
                } else {
                    alert(response.data.message || t("Error updating lap"));
                    throw new Error(response.data.message);
                }
            })
            .then(response => {
                const data = response.data;
                if (data && data.meta) {
                    setMonitoringMeta(data.meta);
                    setMonitoringEvents(data.events || []);
                    setMonitoringAthletes(data.athletes || []);
                }
            })
            .catch(error => {
                console.error('Error updating lap event:', error);
            });
    }

    const handleExportPDF = async () => {
        if (!monitoringId) return;

        try {
            setExportingPDF(true);
            await new Promise(resolve => setTimeout(resolve, 300));

            const captureOptions = {
                quality: 1.0,
                pixelRatio: 2,
                backgroundColor: '#ffffff',
                cacheBust: true,
                skipFonts: true,
                preferredFontFormat: 'woff2' as const,
                filter: (node: HTMLElement) => {
                    if (node.tagName === 'STYLE') return false;
                    if (node.tagName === 'LINK' && node.getAttribute('rel') === 'stylesheet') return false;
                    return true;
                }
            };

            const captureNode = (node: HTMLElement | null): Promise<string | null> => {
                if (!node) return Promise.resolve(null);
                return htmlToImage.toPng(node, captureOptions);
            };

            // ECharts renders asynchronously with animations; html-to-image often captures
            // the chart canvas before it has painted on mobile, producing empty PNGs.
            // Pull the image directly from the ECharts instance instead, waiting for the
            // 'finished' event so the chart has fully rendered.
            const waitForChartReady = (chart: any): Promise<void> => {
                return new Promise<void>((resolve) => {
                    if (!chart) {
                        resolve();
                        return;
                    }
                    let resolved = false;
                    const done = () => {
                        if (resolved) return;
                        resolved = true;
                        try { chart.off('finished', done); } catch {}
                        resolve();
                    };
                    try { chart.on('finished', done); } catch { done(); return; }
                    setTimeout(done, 1500);
                });
            };

            const captureChart = async (
                chartComponent: ReactECharts | null
            ): Promise<string | null> => {
                if (!chartComponent) return null;
                const instance = chartComponent.getEchartsInstance?.();
                if (!instance) return null;
                await waitForChartReady(instance);
                try {
                    return instance.getDataURL({
                        type: 'png',
                        pixelRatio: 2,
                        backgroundColor: '#ffffff',
                    });
                } catch {
                    return null;
                }
            };

            const summary = getSessionSummary();
            const lapEventsForSeries = (summary?.lapDetails || []).map((d: any) => ({
                event_id: d.id,
                event_time: d.eventTime,
                duration: d.durationSeconds,
                kind: d.kind,
            }));
            const shovelfulsForSeries = summary
                ? summary.shovelfulsPerMinute.map((value: number, idx: number) => ({
                      value,
                      timestamp: summary.shovelfulsTimestamps[idx] || null,
                  }))
                : [];
            const blocks = buildSeriesBlocks(lapEventsForSeries, shovelfulsForSeries);
            const seriesBlocks = blocks.filter(b => b.type === 'series') as Extract<SeriesBlock, { type: 'series' }>[];

            const seriesImages: MonitoringSeriesImages[] = await Promise.all(
                seriesBlocks.map(async (block) => {
                    const idx = block.index;
                    const [summaryImg, shovelfulsImg, laptimesImg] = await Promise.all([
                        captureNode(seriesSummaryRefs.current[idx] || null),
                        captureChart(seriesShovelfulsRefs.current[idx] || null),
                        captureChart(seriesLaptimesRefs.current[idx] || null),
                    ]);
                    return {
                        summary: summaryImg,
                        chart_shovelfuls: shovelfulsImg,
                        chart_laptimes: laptimesImg,
                    };
                })
            );

            const response = await exportMonitoringPDF(
                monitoringId,
                seriesImages.length > 0
                    ? { series: seriesImages }
                    : { summary: null, chart_shovelfuls: null, chart_laptimes: null }
            );

            if (response.data.success) {
                const binaryString = atob(response.data.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = response.data.filename || `monitoring-${params.day}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                alert(t("Error generating PDF. Try again later."));
            }
        } catch (error) {
            console.error('Error exporting monitoring PDF:', error);
            alert(t("Error generating PDF. Try again later."));
        } finally {
            setExportingPDF(false);
        }
    };

    const handleToggleEntryMode = () => {
        if (entryMode === 'realtime') {
            // Switching to manual mode - initialize with default times
            setManualStartTime('');
            setManualDuration('');
            setManualLapEntries([]);
            setManualShovefulEntries([]);
            setManualComments('');
            // Preseleccionar todos los atletas
            const allAthleteIds = availableAthletes.map((a: any) => a.athlete_id);
            setManualSelectedAthletes(allAthleteIds);
            setEntryMode('manual');
        } else {
            setEntryMode('realtime');
        }
    }

    // Función para aplicar máscara HH:MM:SS.mmm
    const applyTimeWithMillisecondsMask = (value: string): string => {
        // Eliminar todo excepto números
        const numbers = value.replace(/[^\d]/g, '');

        let result = '';
        for (let i = 0; i < numbers.length && i < 9; i++) {
            if (i === 2 || i === 4) {
                result += ':';
            } else if (i === 6) {
                result += '.';
            }
            result += numbers[i];
        }

        return result;
    }

    // Función para aplicar máscara MM:SS.mmm (para parciales)
    const applyLapTimeMask = (value: string): string => {
        // Eliminar todo excepto números
        const numbers = value.replace(/[^\d]/g, '');

        let result = '';
        for (let i = 0; i < numbers.length && i < 7; i++) {
            if (i === 2) {
                result += ':';
            } else if (i === 4) {
                result += '.';
            }
            result += numbers[i];
        }

        return result;
    }

    // Función para aplicar máscara HH:MM:SS (sin milisegundos)
    const applyTimeWithoutMillisecondsMask = (value: string): string => {
        // Eliminar todo excepto números
        const numbers = value.replace(/[^\d]/g, '');

        let result = '';
        for (let i = 0; i < numbers.length && i < 6; i++) {
            if (i === 2 || i === 4) {
                result += ':';
            }
            result += numbers[i];
        }

        return result;
    }

    // Función para validar formato de tiempo HH:MM:SS.mmm
    const validateTimeFormat = (time: string): boolean => {
        const regex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])\.(\d{3})$/;
        return regex.test(time);
    }

    // Función para validar formato de tiempo de parcial MM:SS.mmm
    const validateLapTimeFormat = (time: string): boolean => {
        const regex = /^([0-5]?[0-9]):([0-5][0-9])\.(\d{3})$/;
        return regex.test(time);
    }

    // Función para convertir tiempo HH:MM:SS.mmm a milisegundos desde medianoche
    const timeToMilliseconds = (time: string): number => {
        const parts = time.split(':');
        const hours = parseInt(parts[0]);
        const minutes = parseInt(parts[1]);
        const secondsParts = parts[2].split('.');
        const seconds = parseInt(secondsParts[0]);
        const milliseconds = parseInt(secondsParts[1] || '0');
        return (hours * 3600 + minutes * 60 + seconds) * 1000 + milliseconds;
    }

    // Función para convertir tiempo de parcial MM:SS.mmm a milisegundos
    const lapTimeToMilliseconds = (time: string): number => {
        const parts = time.split(':');
        const minutes = parseInt(parts[0] || '0');
        const secondsParts = parts[1]?.split('.') || ['0', '0'];
        const seconds = parseInt(secondsParts[0] || '0');
        const milliseconds = parseInt(secondsParts[1] || '0');
        return (minutes * 60 + seconds) * 1000 + milliseconds;
    }

    // Función para combinar día de la URL con hora ingresada
    const combineDateAndTime = (time: string): moment.Moment => {
        const dayStr = moment(params.day).format('YYYY-MM-DD');
        const parts = time.split(':');
        const hours = parseInt(parts[0]);
        const minutes = parseInt(parts[1]);
        const secondsParts = parts[2].split('.');
        const seconds = parseInt(secondsParts[0]);
        const milliseconds = parseInt(secondsParts[1] || '0');

        return moment(dayStr)
            .hours(hours)
            .minutes(minutes)
            .seconds(seconds)
            .milliseconds(milliseconds);
    }

    // Función para calcular hora de fin a partir de hora de inicio + duración
    const calculateEndTime = (startTime: string, duration: string): string => {
        // startTime formato: HH:MM:SS.mmm
        // duration formato: HH:MM:SS.mmm
        const startMoment = combineDateAndTime(startTime);

        const durationParts = duration.split(':');
        const durationHours = parseInt(durationParts[0] || '0');
        const durationMinutes = parseInt(durationParts[1] || '0');
        const secondsParts = durationParts[2]?.split('.') || ['0', '0'];
        const durationSeconds = parseInt(secondsParts[0] || '0');
        const durationMilliseconds = parseInt(secondsParts[1] || '0');

        const endMoment = startMoment.clone()
            .add(durationHours, 'hours')
            .add(durationMinutes, 'minutes')
            .add(durationSeconds, 'seconds')
            .add(durationMilliseconds, 'milliseconds');

        return endMoment.format('HH:mm:ss.SSS');
    }

    // Función para validar formato de duración HH:MM:SS.mmm
    const validateDurationFormat = (duration: string): boolean => {
        const regex = /^([0-9]{1,2}):([0-5][0-9]):([0-5][0-9])\.(\d{3})$/;
        return regex.test(duration);
    }

    // Función para convertir milisegundos a formato HH:MM:SS.mmm
    const formatMillisecondsToTime = (ms: number): string => {
        const hours = Math.floor(ms / 3600000);
        const minutes = Math.floor((ms % 3600000) / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        const milliseconds = ms % 1000;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
    }

    // Calcular la suma de parciales en milisegundos
    const getManualLapsSumMs = (): number => {
        let totalMs = 0;
        manualLapEntries.forEach(lap => {
            if (lap.time && validateLapTimeFormat(lap.time)) {
                totalMs += lapTimeToMilliseconds(lap.time);
            }
        });
        return totalMs;
    };

    // Duración calculada a partir de parciales
    const calculatedDurationFromLaps = (): string => {
        const totalMs = getManualLapsSumMs();
        if (totalMs <= 0) return '';
        return formatMillisecondsToTime(totalMs);
    };

    // Auto-actualizar duración cuando cambian los parciales
    const hasValidLaps = manualLapEntries.some(lap => lap.time && validateLapTimeFormat(lap.time));
    const effectiveDuration = hasValidLaps ? calculatedDurationFromLaps() : manualDuration;

    const handleSubmitManualEntry = () => {
        // Validar campos requeridos
        if (!manualStartTime || !effectiveDuration) {
            alert(t("Please fill in start time and duration"));
            return;
        }

        // Validar formato de tiempos
        if (!validateTimeFormat(manualStartTime)) {
            alert(t("Invalid start time format. Use HH:MM:SS.mmm"));
            return;
        }

        if (!validateDurationFormat(effectiveDuration)) {
            alert(t("Invalid duration format. Use HH:MM:SS.mmm"));
            return;
        }

        if (manualSelectedAthletes.length === 0) {
            alert(t("Please select at least one athlete"));
            return;
        }

        // Calcular hora de fin a partir de inicio + duración
        const calculatedEndTime = calculateEndTime(manualStartTime, effectiveDuration);

        // Validar que la duración sea mayor que 0
        const durationMs = timeToMilliseconds(effectiveDuration);

        if (durationMs <= 0) {
            alert(t("Session duration must be greater than 00:00:00.000"));
            return;
        }

        // Filtrar parciales con valores válidos (ignorar vacíos o incompletos)
        const validLaps = manualLapEntries.filter(lap => lap.time && lap.time.length >= 9);

        // Calcular duración en milisegundos
        const durationParts = effectiveDuration.split(':');
        const durationHours = parseInt(durationParts[0] || '0');
        const durationMinutes = parseInt(durationParts[1] || '0');
        const secondsParts = durationParts[2]?.split('.') || ['0', '0'];
        const durationSeconds = parseInt(secondsParts[0] || '0');
        const durationMilliseconds = parseInt(secondsParts[1] || '0');
        const totalDurationMs = (durationHours * 3600 + durationMinutes * 60 + durationSeconds) * 1000 + durationMilliseconds;

        // Validar parciales (son tiempos INCREMENTALES - duración de cada parcial)
        let accumulatedMs = 0;
        for (let i = 0; i < validLaps.length; i++) {
            const lap = validLaps[i];
            if (!validateLapTimeFormat(lap.time)) {
                alert(t("Invalid lap duration format at position") + ` ${i + 1}. ` + t("Use format MM:SS.mmm (duration of this lap)"));
                return;
            }

            // lap.time es la DURACIÓN de este parcial
            const lapDurationMs = lapTimeToMilliseconds(lap.time);

            // Validar que la duración del parcial sea mayor que 0
            if (lapDurationMs <= 0) {
                alert(
                    t("Lap duration at position") + ` ${i + 1} ` +
                    t("must be greater than 00:00.000")
                );
                return;
            }

            // Acumular el tiempo
            accumulatedMs += lapDurationMs;
        }

        // Filtrar registros de paladas con valores válidos (ignorar vacíos o incompletos)
        const validShovelfuls = manualShovefulEntries.filter(s =>
            s.time && s.time.length >= 9 && s.strokesPerMinute > 0
        );

        // Validar registros de paladas (son tiempos ABSOLUTOS desde el inicio, no incrementales)
        for (let i = 0; i < validShovelfuls.length; i++) {
            const shovelful = validShovelfuls[i];

            // Validar formato MM:SS.mmm
            if (!validateLapTimeFormat(shovelful.time)) {
                alert(t("Invalid stroke time format at position") + ` ${i + 1}. ` + t("Use format MM:SS.mmm (time from session start)"));
                return;
            }

            // shovelful.time es tiempo ABSOLUTO transcurrido desde el inicio
            const shovefulElapsedMs = lapTimeToMilliseconds(shovelful.time);

            // Validar que esté dentro del rango de la duración (debe ser >= 0 y <= duración total)
            if (shovefulElapsedMs < 0 || shovefulElapsedMs > totalDurationMs) {
                alert(
                    t("Stroke time at position") + ` ${i + 1} ` +
                    t("(time from session start) must be between 00:00.000 and session duration") +
                    `\n${t("Session duration")}: ${effectiveDuration}`
                );
                return;
            }
        }

        // Combinar fecha con hora y construir array de eventos
        const events: {event_type: string, event_time: string, event_value: string}[] = [];

        const startMoment = combineDateAndTime(manualStartTime);

        // Calcular endMoment directamente sumando la duración al startMoment
        // Esto maneja correctamente el cruce de medianoche
        const endDurationParts = effectiveDuration.split(':');
        const endDurationHours = parseInt(endDurationParts[0] || '0');
        const endDurationMinutes = parseInt(endDurationParts[1] || '0');
        const endDurationSecondsParts = endDurationParts[2]?.split('.') || ['0', '0'];
        const endDurationSeconds = parseInt(endDurationSecondsParts[0] || '0');
        const endDurationMilliseconds = parseInt(endDurationSecondsParts[1] || '0');

        const endMoment = startMoment.clone()
            .add(endDurationHours, 'hours')
            .add(endDurationMinutes, 'minutes')
            .add(endDurationSeconds, 'seconds')
            .add(endDurationMilliseconds, 'milliseconds');

        // Añadir eventos de parciales (solo los válidos)
        // Los parciales son tiempos INCREMENTALES (duración de cada parcial)
        // Necesitamos acumularlos para obtener el tiempo absoluto
        let accumulatedLapTime = startMoment.clone();
        validLaps.forEach((lap, index) => {
            // lap.time es la DURACIÓN de este parcial en formato MM:SS.mmm (ej: 05:00.000 = 5 minutos)
            // Necesitamos sumar esta duración al tiempo acumulado
            const lapDurationParts = lap.time.split(':');
            const lapDurationMinutes = parseInt(lapDurationParts[0] || '0');
            const lapDurationSecondsParts = lapDurationParts[1]?.split('.') || ['0', '0'];
            const lapDurationSeconds = parseInt(lapDurationSecondsParts[0] || '0');
            const lapDurationMilliseconds = parseInt(lapDurationSecondsParts[1] || '0');

            // Sumar la duración de este parcial al tiempo acumulado
            accumulatedLapTime = accumulatedLapTime.clone()
                .add(lapDurationMinutes, 'minutes')
                .add(lapDurationSeconds, 'seconds')
                .add(lapDurationMilliseconds, 'milliseconds');

            events.push({
                event_type: 'lap',
                event_time: accumulatedLapTime.format('YYYY-MM-DD HH:mm:ss.SSS'),
                event_value: lap.kind === 'rest' ? 'rest' : ''
            });
        });

        // Añadir eventos de paladas (solo los válidos, guardar paladas por minuto con un decimal)
        // Los tiempos de paladas son ABSOLUTOS desde el inicio (no incrementales como los parciales)
        validShovelfuls.forEach((shovelful, index) => {
            // shovelful.time es tiempo ABSOLUTO transcurrido desde el inicio en formato MM:SS.mmm (ej: 15:00.000 = a los 15 minutos del inicio)
            // Necesitamos convertirlo a hora absoluta sumándolo a startMoment
            const elapsedParts = shovelful.time.split(':');
            const elapsedMinutes = parseInt(elapsedParts[0] || '0');
            const elapsedSecondsParts = elapsedParts[1]?.split('.') || ['0', '0'];
            const elapsedSeconds = parseInt(elapsedSecondsParts[0] || '0');
            const elapsedMilliseconds = parseInt(elapsedSecondsParts[1] || '0');

            const shovefulAbsoluteMoment = startMoment.clone()
                .add(elapsedMinutes, 'minutes')
                .add(elapsedSeconds, 'seconds')
                .add(elapsedMilliseconds, 'milliseconds');

            events.push({
                event_type: 'Shovelful',
                event_time: shovefulAbsoluteMoment.format('YYYY-MM-DD HH:mm:ss.SSS'),
                event_value: shovelful.strokesPerMinute.toFixed(1)
            });
        });

        // Construir body siempre con los parámetros legacy (igual que el modo en vivo)
        const body: any = {
            team_id: params.team,
            day: params.day,
            day_moment: params.day_moment,
            day_moment_session: params.day_moment_session,
            start_time: startMoment.format('YYYY-MM-DD HH:mm:ss.SSS'),
            end_time: endMoment.format('YYYY-MM-DD HH:mm:ss.SSS'),
            observations: manualComments,
            athlete_ids: manualSelectedAthletes,
            events: events
        };

        // Si ya hay un monitoring_id, incluirlo para actualizar esa sesión específica
        if (monitoringId) {
            body.monitoring_id = monitoringId;
        }

        updateCoachPlanningMonitorManual(body)
            .then(response => {
                if (response.data.status == 1) {
                    // Guardar el monitoring_id de la respuesta
                    if (response.data.meta_id) {
                        setMonitoringId(response.data.meta_id);
                    }
                    // Recargar los datos para mostrar el resumen
                    return getCoachPlanningMonitoring(params.team, params.day, params.day_moment, params.day_moment_session, response.data.meta_id || monitoringId || undefined);
                } else {
                    alert(response.data.message || t("Error saving manual entry"));
                    throw new Error(response.data.message);
                }
            })
            .then(response => {
                const data = response.data;
                if (data && data.meta) {
                    setMonitoringMeta(data.meta);
                    setMonitoringEvents(data.events || []);
                    setMonitoringAthletes(data.athletes || []);
                }
                setMonitoringState('finished');
                setEntryMode('realtime');
            })
            .catch(error => {
                console.log(error);
            });
    }

    const getSessionSummary = () => {
        if (!monitoringEvents || monitoringEvents.length === 0) {
            return null;
        }

        const startEvent = monitoringEvents.find((event: any) => event.event_type === 'Training start');
        const finishEvent = monitoringEvents.find((event: any) => event.event_type === 'Training finish');
        const lapEvents = monitoringEvents.filter((event: any) => event.event_type === 'lap');
        const shovelfulEvents = monitoringEvents.filter((event: any) => event.event_type === 'Shovelful');

        // Calcular promedio, máximo y mínimo de paladas por minuto (event_value ya contiene paladas/min)
        let averageShovelfulsPerMinute = 0;
        let maxShovelfulsPerMinute = 0;
        let minShovelfulsPerMinute = 0;

        if (shovelfulEvents.length > 0) {
            let totalStrokesPerMinute = 0;
            const strokesPerMinuteValues: number[] = [];

            shovelfulEvents.forEach((event: any) => {
                const strokesPerMin = parseFloat(event.event_value || '0');
                if (strokesPerMin > 0) {
                    totalStrokesPerMinute += strokesPerMin;
                    strokesPerMinuteValues.push(strokesPerMin);
                }
            });

            averageShovelfulsPerMinute = totalStrokesPerMinute / shovelfulEvents.length;
            maxShovelfulsPerMinute = Math.max(...strokesPerMinuteValues);
            minShovelfulsPerMinute = Math.min(...strokesPerMinuteValues);
        }

        let totalDuration = null;
        let durationFormatted = '';
        if (startEvent && finishEvent) {
            const start = moment(startEvent.event_time);
            const finish = moment(finishEvent.event_time);
            const duration = moment.duration(finish.diff(start));
            totalDuration = duration;
            const hours = Math.floor(duration.asHours());
            const minutes = duration.minutes();
            const seconds = duration.seconds();
            const milliseconds = duration.milliseconds();
            durationFormatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
        }

        // Calcular tiempos de parciales
        const lapTimes: number[] = [];
        const lapDetails: { id: number, lapNumber: number, eventTime: string, elapsedFormatted: string, elapsedSeconds: number, durationSeconds: number, durationFormatted: string, kind: 'work' | 'rest' }[] = [];

        if (startEvent && lapEvents.length > 0) {
            let previousTime = moment(startEvent.event_time);
            const startMoment = moment(startEvent.event_time);

            lapEvents.forEach((lap: any, index: number) => {
                const lapTime = moment(lap.event_time);
                const lapDuration = moment.duration(lapTime.diff(previousTime));
                const lapSeconds = lapDuration.asSeconds();
                lapTimes.push(lapSeconds);

                // Tiempo acumulado desde el inicio
                const elapsed = moment.duration(lapTime.diff(startMoment));
                const elapsedMins = Math.floor(elapsed.asMinutes());
                const elapsedSecs = elapsed.seconds();
                const elapsedMillis = elapsed.milliseconds();
                const elapsedFormatted = `${elapsedMins.toString().padStart(2, '0')}:${elapsedSecs.toString().padStart(2, '0')}.${elapsedMillis.toString().padStart(3, '0')}`;

                // Duración del parcial
                const durMins = Math.floor(lapSeconds / 60);
                const durSecs = Math.floor(lapSeconds % 60);
                const durMillis = Math.round((lapSeconds % 1) * 1000);
                const durationFormatted = `${durMins.toString().padStart(2, '0')}:${durSecs.toString().padStart(2, '0')}.${durMillis.toString().padStart(3, '0')}`;

                lapDetails.push({
                    id: lap.id,
                    lapNumber: index + 1,
                    eventTime: lap.event_time,
                    elapsedFormatted,
                    elapsedSeconds: elapsed.asSeconds(),
                    durationSeconds: lapSeconds,
                    durationFormatted,
                    kind: lap.event_value === 'rest' ? 'rest' : 'work'
                });

                previousTime = lapTime;
            });
        }

        // Preparar datos para el gráfico (event_value ya contiene paladas/min)
        const shovelfulsPerMinute: number[] = [];
        const shovelfulsTimestamps: string[] = [];

        if (startEvent) {
            const startTime = moment(startEvent.event_time);

            shovelfulEvents.forEach((event: any, index: number) => {
                const strokesPerMin = parseFloat(event.event_value || '0');
                if (strokesPerMin > 0) {
                    shovelfulsPerMinute.push(strokesPerMin);

                    // Calcular tiempo relativo desde el inicio
                    const eventTime = moment(event.event_time);
                    const elapsed = moment.duration(eventTime.diff(startTime));

                    const elapsedHours = Math.floor(elapsed.asHours());
                    const elapsedMinutes = elapsed.minutes();
                    const elapsedSeconds = elapsed.seconds();
                    const formattedTime = `${elapsedHours.toString().padStart(2, '0')}:${elapsedMinutes.toString().padStart(2, '0')}:${elapsedSeconds.toString().padStart(2, '0')}`;

                    shovelfulsTimestamps.push(formattedTime);
                }
            });
        }

        return {
            startTime: startEvent ? moment(startEvent.event_time).format('HH:mm:ss.SSS') : null,
            finishTime: finishEvent ? moment(finishEvent.event_time).format('HH:mm:ss.SSS') : null,
            durationFormatted,
            totalDuration,
            totalLaps: lapEvents.length,
            averageShovelfulsPerMinute: averageShovelfulsPerMinute,
            maxShovelfulsPerMinute: maxShovelfulsPerMinute,
            minShovelfulsPerMinute: minShovelfulsPerMinute,
            lapTimes,
            lapDetails,
            shovelfulsPerMinute,
            shovelfulsTimestamps
        };
    }

    return (
        <div className="coach-training-console-container">
            <h1>
                { t("Monitoring training session") }
                <br />
                <strong>{ moment(params.day).format('D MMMM') } { t(params.day_moment) }</strong>
            </h1>
            { !loaded && <LoadingSpinner />}

            {/* Selector de monitorizaciones existentes */}
            { loaded && showMonitoringSelector && (
                <div className="monitoring-selector">
                    <h3>{t("Existing monitoring sessions")}</h3>
                    <p className="text-muted">{t("There are monitoring sessions for this training session. You can continue with an existing one or create a new one.")}</p>

                    <div className="monitoring-list mb-4">
                        {monitoringList.map((session: any) => (
                            <div key={session.monitoring_id} className="monitoring-item">
                                <div className="monitoring-info">
                                    <div className="monitoring-header">
                                        <strong>{t("Monitoring")} #{session.monitoring_id}</strong>
                                    </div>
                                    <div className="monitoring-details">
                                        <span><i className="bi bi-people"></i> {session.athletes.length} {session.athletes.length === 1 ? t("athlete") : t("athletes")}</span>
                                        {session.start_time && <span><i className="bi bi-clock"></i> {moment(session.start_time).format('HH:mm:ss')}</span>}
                                        {session.finish_time && <span> - {moment(session.finish_time).format('HH:mm:ss')}</span>}
                                        {session.start_time && session.finish_time && (() => {
                                            const duration = moment.duration(moment(session.finish_time).diff(moment(session.start_time)));
                                            const hours = Math.floor(duration.asHours());
                                            const minutes = duration.minutes();
                                            const seconds = duration.seconds();
                                            const durationStr = hours > 0
                                                ? `${hours}h ${minutes}m ${seconds}s`
                                                : minutes > 0
                                                    ? `${minutes}m ${seconds}s`
                                                    : `${seconds}s`;
                                            return <span><i className="bi bi-hourglass-split"></i> {durationStr}</span>;
                                        })()}
                                        {session.lap_count !== undefined && <span><i className="bi bi-flag"></i> {session.lap_count} {t("laps")}</span>}
                                        {session.avg_shovelfuls !== undefined && session.avg_shovelfuls > 0 && <span><i className="bi bi-activity"></i> {parseFloat(session.avg_shovelfuls).toFixed(1)} {t("strokes/min")}</span>}
                                    </div>
                                    <div className="monitoring-athletes">
                                        <span
                                            className="athletes-count-toggle"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setExpandedMonitoringAthletes(prev =>
                                                    prev.includes(session.monitoring_id)
                                                        ? prev.filter(id => id !== session.monitoring_id)
                                                        : [...prev, session.monitoring_id]
                                                );
                                            }}
                                        >
                                            <i className={`bi ${expandedMonitoringAthletes.includes(session.monitoring_id) ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>{' '}
                                            {session.athletes.length === 1
                                                ? `1 ${t("athlete count singular")}`
                                                : `${session.athletes.length} ${t("athlete count plural")}`
                                            }
                                        </span>
                                        {expandedMonitoringAthletes.includes(session.monitoring_id) && (
                                            <div className="athletes-list mt-1">
                                                {session.athletes.map((athlete: any, idx: number) => (
                                                    <span key={athlete.athlete_id} className="athlete-name">
                                                        {athlete.athlete_name}{idx < session.athletes.length - 1 ? ', ' : ''}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {session.meta && session.meta.observations && (
                                        <div className="monitoring-observations">
                                            <i className="bi bi-chat-text"></i> {session.meta.observations.length > 100 ? session.meta.observations.substring(0, 100) + '...' : session.meta.observations}
                                        </div>
                                    )}
                                </div>
                                <div className="monitoring-actions">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => loadMonitoringSession(session.monitoring_id)}
                                    >
                                        {t("View")}
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDeleteMonitoring(session.monitoring_id)}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <Button
                            variant="success"
                            size="lg"
                            onClick={createNewMonitoring}
                        >
                            <i className="bi bi-plus-circle"></i> {t("Create new monitoring session")}
                        </Button>
                    </div>
                </div>
            )}

            { loaded && !showMonitoringSelector &&
                <>
                    {/* Botón para volver a la lista de monitorizaciones */}
                    { monitoringList.length > 0 && (
                        <div className="mb-3" style={{textAlign: 'center'}}>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={backToMonitoringList}
                            >
                                <i className="bi bi-arrow-left"></i> {t("Back to monitoring list")}
                            </Button>
                        </div>
                    )}

                    {/* Toggle button for entry mode */}
                    { monitoringState === 'no_monitoring' && (
                        <div className="entry-mode-toggle mb-3" style={{textAlign: 'center'}}>
                            <Button
                                variant={entryMode === 'manual' ? 'primary' : 'outline-primary'}
                                onClick={handleToggleEntryMode}
                            >
                                <i className={`bi ${entryMode === 'manual' ? 'bi-stopwatch' : 'bi-pencil-square'}`}></i>{' '}
                                {entryMode === 'manual' ? t("Switch to real-time mode") : t("Switch to manual entry")}
                            </Button>
                        </div>
                    )}

                    {/* Manual entry form */}
                    { entryMode === 'manual' && monitoringState === 'no_monitoring' && (
                        <div className="coach-training-console-manual-content">
                            <div className="manual-entry-form">
                                <h4>{t("Manual session data entry")}</h4>
                                <Form onSubmit={(e) => { e.preventDefault(); handleSubmitManualEntry(); }}>
                                    {/* Start time and Duration in same row */}
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>{t("Start time")} (HH:MM:SS.mmm)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={manualStartTime}
                                                    onChange={(e) => setManualStartTime(applyTimeWithMillisecondsMask(e.target.value))}
                                                    placeholder="00:00:00.000"
                                                    maxLength={12}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>{t("Duration")} (HH:MM:SS.mmm)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={effectiveDuration}
                                                    onChange={(e) => setManualDuration(applyTimeWithMillisecondsMask(e.target.value))}
                                                    placeholder="00:00:00.000"
                                                    maxLength={12}
                                                    readOnly={hasValidLaps}
                                                    className={hasValidLaps ? 'bg-light' : ''}
                                                />
                                                <Form.Text className="text-muted">
                                                    {hasValidLaps
                                                        ? t("Calculated from lap times")
                                                        : t("Total duration of the session")
                                                    }
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {/* Lap entries section */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("Lap times")} ({t("optional")})</Form.Label>
                                        <Form.Text className="d-block mb-2 text-muted">
                                            {t("Enter duration of each lap (MM:SS.mmm)")}
                                        </Form.Text>
                                        <div className="manual-lap-entries">
                                            {manualLapEntries.map((lap, index) => (
                                                <Row key={index} className="mb-2 align-items-center">
                                                    <Col>
                                                        <Form.Control
                                                            type="text"
                                                            value={lap.time}
                                                            onChange={(e) => {
                                                                const newLaps = [...manualLapEntries];
                                                                newLaps[index].time = applyLapTimeMask(e.target.value);
                                                                setManualLapEntries(newLaps);
                                                            }}
                                                            placeholder="00:00.000"
                                                            maxLength={9}
                                                        />
                                                    </Col>
                                                    <Col xs="auto">
                                                        <Form.Select
                                                            value={lap.kind || 'work'}
                                                            onChange={(e) => {
                                                                const newLaps = [...manualLapEntries];
                                                                newLaps[index].kind = e.target.value as 'work' | 'rest';
                                                                setManualLapEntries(newLaps);
                                                            }}
                                                            style={{ width: 'auto' }}
                                                        >
                                                            <option value="work">{t("Work")}</option>
                                                            <option value="rest">{t("Rest")}</option>
                                                        </Form.Select>
                                                    </Col>
                                                    <Col xs="auto">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => {
                                                                const newLaps = manualLapEntries.filter((_, i) => i !== index);
                                                                setManualLapEntries(newLaps);
                                                            }}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ))}
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    setManualLapEntries([...manualLapEntries, { time: '', kind: 'work' }]);
                                                }}
                                            >
                                                <i className="bi bi-plus-circle"></i> {t("Add lap")}
                                            </Button>
                                        </div>
                                    </Form.Group>

                                    {/* Shovelful entries section */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("Stroke records")} ({t("optional")})</Form.Label>
                                        <Form.Text className="d-block mb-2 text-muted">
                                            {t("Enter elapsed time from start (MM:SS.mmm)")}
                                        </Form.Text>
                                        <div className="manual-shovelful-entries">
                                            {manualShovefulEntries.map((shovelful, index) => (
                                                <Row key={index} className="mb-2 align-items-center">
                                                    <Col md={6}>
                                                        <Form.Control
                                                            type="text"
                                                            value={shovelful.time}
                                                            onChange={(e) => {
                                                                const newShovefuls = [...manualShovefulEntries];
                                                                newShovefuls[index].time = applyLapTimeMask(e.target.value);
                                                                setManualShovefulEntries(newShovefuls);
                                                            }}
                                                            placeholder="00:00.000"
                                                            maxLength={9}
                                                        />
                                                        <Form.Text className="text-muted">{t("Elapsed time")}</Form.Text>
                                                    </Col>
                                                    <Col md={5}>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.1"
                                                            min="0"
                                                            value={shovelful.strokesPerMinute}
                                                            onChange={(e) => {
                                                                const newShovefuls = [...manualShovefulEntries];
                                                                newShovefuls[index].strokesPerMinute = parseFloat(e.target.value) || 0;
                                                                setManualShovefulEntries(newShovefuls);
                                                            }}
                                                            placeholder={t("Strokes per minute")}
                                                        />
                                                        <Form.Text className="text-muted">
                                                            {t("Number of strokes per minute")}
                                                        </Form.Text>
                                                    </Col>
                                                    <Col xs="auto">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => {
                                                                const newShovefuls = manualShovefulEntries.filter((_, i) => i !== index);
                                                                setManualShovefulEntries(newShovefuls);
                                                            }}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ))}
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    setManualShovefulEntries([...manualShovefulEntries, { time: '', strokesPerMinute: 0 }]);
                                                }}
                                            >
                                                <i className="bi bi-plus-circle"></i> {t("Add stroke record")}
                                            </Button>
                                        </div>
                                    </Form.Group>

                                    {/* Athletes selection */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("Participating athletes")}</Form.Label>
                                        <div className="manual-athletes-selection">
                                            {availableAthletes.length > 0 && (
                                                <Form.Check
                                                    type="checkbox"
                                                    id="manual-select-all-athletes"
                                                    label={<strong>{t("Select all")}</strong>}
                                                    checked={availableAthletes.length > 0 && availableAthletes.every((a: any) => manualSelectedAthletes.includes(a.athlete_id))}
                                                    onChange={() => {
                                                        const allIds = availableAthletes.map((a: any) => a.athlete_id);
                                                        const allSelected = allIds.every(id => manualSelectedAthletes.includes(id));
                                                        setManualSelectedAthletes(allSelected ? [] : allIds);
                                                    }}
                                                    className="mb-3 pb-2 border-bottom"
                                                />
                                            )}
                                            {availableAthletes.map((athlete: any) => (
                                                <Form.Check
                                                    key={athlete.athlete_id}
                                                    type="checkbox"
                                                    id={`manual-athlete-${athlete.athlete_id}`}
                                                    label={
                                                        <span>
                                                            {athlete.dorsal && <strong>#{athlete.dorsal}</strong>} {athlete.athlete_name}
                                                        </span>
                                                    }
                                                    checked={manualSelectedAthletes.includes(athlete.athlete_id)}
                                                    onChange={() => {
                                                        if (manualSelectedAthletes.includes(athlete.athlete_id)) {
                                                            setManualSelectedAthletes(manualSelectedAthletes.filter(id => id !== athlete.athlete_id));
                                                        } else {
                                                            setManualSelectedAthletes([...manualSelectedAthletes, athlete.athlete_id]);
                                                        }
                                                    }}
                                                    className="mb-2"
                                                />
                                            ))}
                                        </div>
                                        <small className="text-muted">
                                            {manualSelectedAthletes.length === 1
                                                ? `1 ${t("athlete selected")}`
                                                : `${manualSelectedAthletes.length} ${t("athletes selected")}`
                                            }
                                        </small>
                                    </Form.Group>

                                    {/* Comments */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t("Coach comments")} ({t("optional")})</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            value={manualComments}
                                            onChange={(e) => setManualComments(e.target.value)}
                                            placeholder={t("Add your comments about this training session...")}
                                        />
                                    </Form.Group>

                                    <div className="d-grid gap-2">
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            type="submit"
                                        >
                                            {t("Save manual entry")}
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    )}

                    {/* Results container - shown when finished */}
                    { monitoringState === 'finished' && (() => {
                        const summary = getSessionSummary();
                        return summary ? (
                            <div className="coach-training-console-results" ref={resultsRef}>
                                <div className="session-summary">
                                    <h3>{ t("Session Summary") }</h3>

                                    {/* Mostrar atletas participantes */}
                                    {monitoringAthletes.length > 0 && (
                                        <div className="participating-athletes mb-3 no-print">
                                            <strong>{t("Participating athletes")}:</strong>{' '}
                                            <span
                                                className="athletes-count-toggle"
                                                onClick={() => setShowAthleteNames(!showAthleteNames)}
                                            >
                                                <i className={`bi ${showAthleteNames ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>{' '}
                                                {monitoringAthletes.length === 1
                                                    ? `1 ${t("athlete count singular")}`
                                                    : `${monitoringAthletes.length} ${t("athlete count plural")}`
                                                }
                                            </span>
                                            {showAthleteNames && (
                                                <div className="athletes-list mt-2">
                                                    {monitoringAthletes.map((athlete: any) => (
                                                        <a
                                                            key={athlete.athlete_id}
                                                            href={`#/athletes/${athlete.athlete_id}/view`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="athlete-badge"
                                                        >
                                                            {athlete.athlete_name}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {(() => {
                                        const lapEventsForSeries = summary.lapDetails.map((d: any) => ({
                                            event_id: d.id,
                                            event_time: d.eventTime,
                                            duration: d.durationSeconds,
                                            kind: d.kind,
                                        }));
                                        const shovelfulsForSeries = summary.shovelfulsPerMinute.map((value: number, idx: number) => ({
                                            value,
                                            timestamp: summary.shovelfulsTimestamps[idx] || null,
                                        }));
                                        const blocks = buildSeriesBlocks(lapEventsForSeries, shovelfulsForSeries);
                                        const workSeriesCount = blocks.filter(b => b.type === 'series').length;

                                        if (blocks.length === 0) {
                                            return (
                                                <div className="summary-boxes" ref={(el) => { seriesSummaryRefs.current[1] = el; }}>
                                                    <div className="summary-box">
                                                        <div className="label">{ t("Start") }</div>
                                                        <div className="value">{summary.startTime}</div>
                                                    </div>
                                                    <div className="summary-box">
                                                        <div className="label">{ t("Finish") }</div>
                                                        <div className="value">{summary.finishTime}</div>
                                                    </div>
                                                    <div className="summary-box">
                                                        <div className="label">{ t("Duration") }</div>
                                                        <div className="value">{summary.durationFormatted}</div>
                                                    </div>
                                                    <div className="summary-box">
                                                        <div className="label">{ t("Laps") }</div>
                                                        <div className="value">{summary.totalLaps}</div>
                                                    </div>
                                                    <div className="summary-box">
                                                        <div className="label">{ t("Average strokes/min") }</div>
                                                        <div className="value">{summary.averageShovelfulsPerMinute.toFixed(1)}</div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return blocks.map((block: SeriesBlock, bi: number) => {
                                            if (block.type === 'rest') {
                                                return (
                                                    <div key={`rest-${bi}`} className="series-rest-band mb-3">
                                                        <strong>{t("Recovery")}:</strong> {formatSeriesDuration(block.duration, true)}
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div key={`series-${block.index}`} className="series-block mb-4">
                                                    {workSeriesCount > 1 && (
                                                        <h5 className="series-heading">{t("Series")} {block.index}</h5>
                                                    )}
                                                    <div className="summary-boxes" ref={(el) => { seriesSummaryRefs.current[block.index] = el; }}>
                                                        <div className="summary-box">
                                                            <div className="label">{t("Duration")}</div>
                                                            <div className="value">{formatSeriesDuration(block.totalDurationSeconds, true)}</div>
                                                        </div>
                                                        <div className="summary-box">
                                                            <div className="label">{t("Laps")}</div>
                                                            <div className="value">{block.lapDurations.length}</div>
                                                        </div>
                                                        <div className="summary-box">
                                                            <div className="label">{t("Average strokes/min")}</div>
                                                            <div className="value">{block.avgShovelfuls !== null ? block.avgShovelfuls.toFixed(1) : '-'}</div>
                                                        </div>
                                                    </div>
                                                    <div className="charts-container">
                                                        {block.shovelfuls.length > 0 && (
                                                            <div className="chart-wrapper">
                                                                <MonitoringShovelfulsChart
                                                                    ref={(el) => { seriesShovelfulsRefs.current[block.index] = el; }}
                                                                    data={block.shovelfuls}
                                                                    timestamps={block.shovelfulsTimestamps} />
                                                            </div>
                                                        )}
                                                        {block.lapDurations.length > 0 && (
                                                            <div className="chart-wrapper">
                                                                <MonitoringLaptimesChart
                                                                    ref={(el) => { seriesLaptimesRefs.current[block.index] = el; }}
                                                                    data={block.lapDurations} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}

                                    {/* Tabla de parciales */}
                                    {summary.lapDetails.length > 0 && (
                                        <div className="lap-details-table mt-4 no-print">
                                            <strong>{t("Lap details")}</strong>
                                            <table className="table table-sm mt-2">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>{t("Elapsed")}</th>
                                                        <th>{t("Lap time")}</th>
                                                        <th className="no-print"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {summary.lapDetails.map((lap: any) => (
                                                        <tr key={lap.id} className={lap.kind === 'rest' ? 'lap-row-rest' : ''}>
                                                            <td>
                                                                {lap.lapNumber}
                                                                {lap.kind === 'rest' && (
                                                                    <span className="badge bg-warning text-dark ms-2">{t("Rest")}</span>
                                                                )}
                                                            </td>
                                                            <td>{lap.elapsedFormatted}</td>
                                                            <td>{lap.durationFormatted}</td>
                                                            <td className="no-print">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    className="me-1"
                                                                    onClick={() => handleEditLapClick(lap)}
                                                                    title={t("Edit lap")}
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteLapEvent(lap.id)}
                                                                    title={t("Delete lap")}
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* Comentarios post-sesión */}
                                    <div className="post-session-comments mt-4 no-print">
                                        <strong>{t("Coach comments")}:</strong>
                                        {!isEditingComments ? (
                                            <div className="comments-display">
                                                {postSessionComments ? (
                                                    <p className="mb-2">{postSessionComments}</p>
                                                ) : (
                                                    <p className="text-muted mb-2"><em>{t("No comments added yet")}</em></p>
                                                )}
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="no-print"
                                                    onClick={() => setIsEditingComments(true)}
                                                >
                                                    <i className="bi bi-pencil"></i> {postSessionComments ? t("Edit comments") : t("Add comments")}
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="comments-edit">
                                                <Form.Group className="mt-2">
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={4}
                                                        value={postSessionComments}
                                                        onChange={(e) => setPostSessionComments(e.target.value)}
                                                        placeholder={t("Add your comments about this training session...")}
                                                    />
                                                </Form.Group>
                                                <div className="mt-2">
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        className="me-2"
                                                        onClick={handleSaveComments}
                                                    >
                                                        <i className="bi bi-check-lg"></i> {t("Save")}
                                                    </Button>
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => {
                                                            // Restaurar el valor original si cancela
                                                            setPostSessionComments(monitoringMeta?.observations || '');
                                                            setIsEditingComments(false);
                                                        }}
                                                    >
                                                        {t("Cancel")}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action buttons */}
                                <div className="d-flex justify-content-center gap-3 mt-4 pdf-export-btn">
                                    <Button
                                        variant="outline-success"
                                        className="btn-lg"
                                        onClick={handleExportPDF}
                                        disabled={exportingPDF}
                                    >
                                        {exportingPDF ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                {t("Generating...")}
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-file-earmark-pdf"></i> {t("Export PDF")}
                                            </>
                                        )}
                                    </Button>
                                    <Button className="reset btn-lg" onClick={handleReset}>{ t("Reset") }</Button>
                                </div>
                            </div>
                        ) : null;
                    })()}

                    {/* Real-time monitoring content */}
                    { monitoringState !== 'finished' && entryMode === 'realtime' && (
                        <div className={`coach-training-console-content ${monitoringState === 'monitoring' || monitoringState === 'pause' ? 'monitoring-active' : ''}`}>

                            {/* Header fijo: cronómetro + controles */}
                            <div className="monitoring-header-fixed">
                                <div style={{textAlign: 'center'}}>
                                    <div className="start-time">
                                        { monitoringState !== 'no_monitoring' &&
                                            <span>{ t("Start at") }: { moment(startDateTime).format('HH:mm:ss') }</span>
                                        }
                                    </div>
                                    <div className="stopwatch-display">
                                        <span>{hours.toString().padStart(2, '0')}</span>:<span>{minutes.toString().padStart(2, '0')}</span>:<span>{seconds.toString().padStart(2, '0')}</span>:<span>{milliseconds.toString().padStart(3, '0')}</span>
                                    </div>

                                    {/* Estado: monitoring */}
                                    { monitoringState === 'monitoring' &&
                                        <div className="control-buttons">
                                            <Button className="pause btn-lg" onClick={handlePause}>{ t("Pause") }</Button>
                                            <Button className="finish btn-lg" onClick={handleFinish}>{ t("Finish") }</Button>
                                        </div>
                                    }

                                    {/* Estado: pause */}
                                    { monitoringState === 'pause' &&
                                        <div className="control-buttons">
                                            <Button className="resume btn-lg" onClick={handleResume}>{ t("Resume") }</Button>
                                            <Button className="finish btn-lg" onClick={handleFinish}>{ t("Finish") }</Button>
                                        </div>
                                    }
                                </div>
                            </div>

                            {/* Estado: no_monitoring - setup */}
                            { monitoringState === 'no_monitoring' &&
                                <div style={{textAlign: 'center'}}>
                                    <div className="setup-shovelfuls">
                                        <div className="setup-label">{ t("Strokes per sample") }</div>
                                        <div className="shovelfuls-counter">
                                            <Button
                                                className="btn-sm"
                                                onClick={() => handleChangeShovelfulsCount(-1)}>
                                                -
                                            </Button>
                                            <div className="shovelfuls-value">
                                                { shovelfulsCount }
                                            </div>
                                            <Button
                                                className="btn-sm"
                                                onClick={() => handleChangeShovelfulsCount(1)}>
                                                +
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Información de atletas */}
                                    <div className="athletes-info mb-3">
                                        <p className="text-muted mb-2">
                                            { t("This monitoring will be performed for all team athletes") }.
                                        </p>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleOpenAthleteSelector}
                                            className="mb-3"
                                        >
                                            { t("Select session athletes") }
                                        </Button>
                                        {selectedAthleteIds.length > 0 && (
                                            <div className="selected-athletes-preview">
                                                <small>
                                                    {selectedAthleteIds.length === 1
                                                        ? <><strong>1</strong> {t("athlete selected")}</>
                                                        : <><strong>{selectedAthleteIds.length}</strong> {t("athletes selected")}</>
                                                    }
                                                </small>
                                            </div>
                                        )}
                                    </div>

                                    <Button className="start btn-lg" onClick={handleConfirmStart}>{ t("Start") }</Button>
                                </div>
                            }

                            {/* Zona media scrollable: últimos registros */}
                            { monitoringState === 'monitoring' &&
                                <div className="monitoring-data-area">
                                    {shovelfulsRecords.length > 0 && (
                                        <div className="shovelfuls-records">
                                            {shovelfulsRecords.slice(-2).reverse().map((record, index) => {
                                                const strokesPerMinute = parseFloat(record);
                                                const isLast = index === 0;
                                                return (
                                                    <div key={index} className={`stroke-record ${isLast ? 'last' : 'previous'}`}>
                                                        <div className="record-label">
                                                            {isLast ? t("Last") : t("Previous")}
                                                        </div>
                                                        <div className="record-value">
                                                            {strokesPerMinute.toFixed(1)}
                                                        </div>
                                                        <div className="record-unit">
                                                            { t("strokes/min") }
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <div className="lap-times-display">
                                        {lastLapTime && (
                                            <div
                                                className={`lap-time-item last ${confirmDeleteLastLap ? 'confirm-delete' : ''} ${lastLapKind === 'rest' ? 'lap-kind-rest' : 'lap-kind-work'}`}
                                                onClick={() => lastLapEventId && setConfirmDeleteLastLap(!confirmDeleteLastLap)}
                                                style={{ cursor: lastLapEventId ? 'pointer' : 'default' }}
                                            >
                                                <div className="lap-header">
                                                    <span className="lap-label">
                                                        {lastLapKind === 'rest' ? t("Last rest") : t("Last lap")}
                                                    </span>
                                                    <span className="lap-value">{lastLapTime}</span>
                                                </div>
                                                {lastLapDiff && !confirmDeleteLastLap && (
                                                    <div className="lap-diff">{lastLapDiff}</div>
                                                )}
                                                {confirmDeleteLastLap && (
                                                    <div className="lap-delete-confirm">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteLastLap(); }}
                                                        >
                                                            { t("Delete") }
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteLastLap(false); }}
                                                        >
                                                            { t("Cancel") }
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {previousLapTime && (
                                            <div className={`lap-time-item previous ${previousLapKind === 'rest' ? 'lap-kind-rest' : 'lap-kind-work'}`}>
                                                <div className="lap-header">
                                                    <span className="lap-label">
                                                        {previousLapKind === 'rest' ? t("Previous rest") : t("Previous lap")}
                                                    </span>
                                                    <span className="lap-value">{previousLapTime}</span>
                                                </div>
                                                {previousLapDiff && (
                                                    <div className="lap-diff">{previousLapDiff}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }

                            {/* Footer fijo: botones de acción */}
                            { monitoringState === 'monitoring' &&
                                <div className="monitoring-actions-fixed">
                                    <div className="shovelfuls-button">
                                        {!isRecordingShovelfuls ? (
                                            <Button className="btn-lg btn-success" onClick={handleStartShovelfulsRecording}>
                                                { t("Start stroke recording") }
                                            </Button>
                                        ) : (
                                            <Button className="btn-lg btn-danger" onClick={handleFinishShovelfulsRecording}>
                                                { t("Finish stroke recording") }
                                            </Button>
                                        )}
                                        <div className="shovelfuls-count-badge">{ shovelfulsCount } { t("strokes") }</div>
                                    </div>
                                    <div className="lap-buttons-group">
                                        <Button className="lap-button btn-lg" onClick={ () => handleLapClick('work') }>{ t("Register lap") }</Button>
                                        <Button className="rest-button btn-lg" onClick={ handleRestClick }>{ t("Register rest") }</Button>
                                    </div>
                                </div>
                            }
                        </div>
                    )}
                </>
            }

            {/* Modal de selección de atletas */}
            <Modal show={showAthleteSelector} onHide={handleCloseAthleteSelector} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t("Select participating athletes")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Campo de búsqueda */}
                    {availableAthletes.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder={t("Search athletes by name...")}
                                value={athleteSearchTerm}
                                onChange={(e) => setAthleteSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </Form.Group>
                    )}

                    <div className="athlete-selection-list">
                        {availableAthletes.length === 0 && (
                            <p className="text-muted">{t("No athletes available for this team")}</p>
                        )}
                        {(() => {
                            const filteredAthletes = getFilteredAthletes();
                            const filteredIds = filteredAthletes.map((a: any) => a.athlete_id);
                            const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selectedAthleteIds.includes(id));

                            return (
                                <>
                                    {filteredAthletes.length > 0 && (
                                        <Form.Check
                                            type="checkbox"
                                            id="select-all-athletes"
                                            label={<strong>{t("Select all")}{athleteSearchTerm && ` (${filteredAthletes.length})`}</strong>}
                                            checked={allFilteredSelected}
                                            onChange={handleToggleAllAthletes}
                                            className="mb-3 pb-2 border-bottom"
                                        />
                                    )}
                                    {filteredAthletes.map((athlete: any) => (
                                        <Form.Check
                                            key={athlete.athlete_id}
                                            type="checkbox"
                                            id={`athlete-${athlete.athlete_id}`}
                                            label={
                                                <span>
                                                    {athlete.dorsal && <strong>#{athlete.dorsal}</strong>} {athlete.athlete_name}
                                                </span>
                                            }
                                            checked={selectedAthleteIds.includes(athlete.athlete_id)}
                                            onChange={() => handleToggleAthlete(athlete.athlete_id)}
                                            className="mb-2"
                                        />
                                    ))}
                                    {filteredAthletes.length === 0 && athleteSearchTerm && (
                                        <p className="text-muted">{t("No athletes found")}</p>
                                    )}
                                </>
                            );
                        })()}
                    </div>

                    <div className="mt-3">
                        <small className="text-muted">
                            {selectedAthleteIds.length === 1
                                ? `1 ${t("athlete selected")}`
                                : `${selectedAthleteIds.length} ${t("athletes selected")}`
                            }
                        </small>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseAthleteSelector}>
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleCloseAthleteSelector}
                        disabled={selectedAthleteIds.length === 0}
                    >
                        {t("Accept")}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Modal para editar parcial */}
            <Modal show={showEditLapModal} onHide={() => setShowEditLapModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Edit lap")} #{editingLap?.lapNumber}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">{t("Edit the elapsed time from the start of the session")}</p>
                    <Row>
                        <Col>
                            <Form.Group>
                                <Form.Label>{t("Minutes")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={editLapMinutes}
                                    onChange={(e) => setEditLapMinutes(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group>
                                <Form.Label>{t("Seconds")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={editLapSeconds}
                                    onChange={(e) => setEditLapSeconds(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group>
                                <Form.Label>{t("Milliseconds")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    max="999"
                                    value={editLapMilliseconds}
                                    onChange={(e) => setEditLapMilliseconds(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mt-3">
                        <Form.Label>{t("Lap type")}</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                id="edit-lap-kind-work"
                                name="edit-lap-kind"
                                label={t("Work")}
                                checked={editLapKind === 'work'}
                                onChange={() => setEditLapKind('work')}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                id="edit-lap-kind-rest"
                                name="edit-lap-kind"
                                label={t("Rest")}
                                checked={editLapKind === 'rest'}
                                onChange={() => setEditLapKind('rest')}
                            />
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditLapModal(false)}>
                        {t("Cancel")}
                    </Button>
                    <Button variant="primary" onClick={handleSaveEditLap}>
                        {t("Save")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}